/**
 * Motion tokens: the one place to tune animation timing, easing, stagger,
 * entrance distances and scroll thresholds.
 *
 * This is the JS source of truth, read by the anime.js hooks (`4-components/
 * animations/anime.ts`) and the homepage scenes (HeroTurbine, the bentos,
 * StatsCounter, CaseShowcase, LayerStack). anime.js / canvas / SVG can't read
 * CSS custom properties, so, exactly like `palette.ts` mirrors the `@theme`
 * colour ramp, these mirror the `--dur-*` / `--ease-*` custom properties in
 * `styles/index.css` that drive the CSS-only animations. Keep the two in sync.
 *
 * EASE values are anime.js easing strings (not CSS cubic-beziers); DURATION /
 * STAGGER are milliseconds; DISTANCE is px of entrance travel; THRESHOLD is an
 * IntersectionObserver rootMargin.
 */

/** Milliseconds. `reveal`/`cell` are the scroll-in fades; `slow`/`base`/`fast` the shared UI transitions. */
export const DURATION = {
  fast: 300,
  base: 500,
  slow: 700,
  reveal: 650,
  cell: 720,
  rollUp: 1400,
} as const;

/** anime.js easing strings. `out`/`inOut` are the site defaults; `drift` the slow ambient loops. */
export const EASE = {
  out: "out(3)",
  outSoft: "out(2)",
  outSharp: "out(4)",
  inOut: "inOut(3)",
  inOutSoft: "inOut(2)",
  drift: "inOutSine",
  quadOut: "outQuad",
  quadInOut: "inOutQuad",
  linear: "linear",
} as const;

/** Per-item delay step (ms) for staggered reveals. */
export const STAGGER = {
  tight: 80,
  base: 90,
  loose: 110,
} as const;

/** Entrance travel in px (translateY/X of a fade-slide). */
export const DISTANCE = {
  reveal: 24,
  revealLg: 32,
} as const;

/** IntersectionObserver rootMargins. `reveal` fires a touch early; `loop` pauses off-screen scenes; `spotlight` is the centre band. */
export const THRESHOLD = {
  reveal: "0px 0px -12% 0px",
  loop: "0px 0px -5% 0px",
  spotlight: "-45% 0px -45% 0px",
} as const;
